import InlineSVG from "react-inlinesvg";
import { useProjectStore } from "src/stores/projectStore";
import { useEffect, useRef, useState } from "react";
// @ts-ignore
import styled from "styled-components/macro";

function CharacterGenerationAnimation() {
  const columnWidths = useProjectStore((state) => state.columnWidths);
  const headRef = useRef(null);
  const headIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const faceIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [currentHead, setCurrentHead] = useState(0);
  const [currentFace, setCurrentFace] = useState(0);
  const blankHead = "/assets/head-blank.svg";

  const heads = [
    "/assets/head-beret.svg", //
    "/assets/head-top-hat.svg",
    "/assets/head-cap.svg",
    "/assets/head-bandaid.svg",
    "/assets/head-witch-hat.svg",
    "/assets/head-bowler-hat.svg",
  ];

  const faces = [
    "/assets/face-glasses.svg", //
    "/assets/face-lashes.svg",
    "/assets/face-shades.svg",
    "/assets/face-mask.svg",
    "/assets/face-stache.svg",
  ];

  const getNextIndexRotating = (currentIdx: number, collection: string[]) => {
    let nextIdx = currentIdx + 1;
    if (nextIdx >= collection.length) {
      nextIdx = 0;
    }
    return nextIdx;
  };

  const handleHeadClick = () => {
    if (headIntervalRef.current) {
      clearInterval(headIntervalRef.current);
      headIntervalRef.current = null;
    }
    setCurrentHead((prev) => getNextIndexRotating(prev, heads));
  };

  const handleFaceClick = () => {
    if (faceIntervalRef.current) {
      clearInterval(faceIntervalRef.current);
      faceIntervalRef.current = null;
    }
    setCurrentFace((prev) => getNextIndexRotating(prev, faces));
  };

  useEffect(() => {
    if (!headRef.current) return;

    const handleCursorPosition = (e: MouseEvent) => {
      const rect = headRef.current?.getBoundingClientRect();
      if (!rect) return;
      const { left, width, height, top } = rect;
      const centerOfHeadX = left + width / 2;
      const centerOfHeadY = top + height / 2 + 50;
      const mouseDistanceFromCenterOfHeadX = e.clientX - centerOfHeadX;
      const mouseDistanceFromCenterOfHeadY = e.clientY - centerOfHeadY;
      const percentageFromCenterOfHeadX = (mouseDistanceFromCenterOfHeadX / centerOfHeadX) * 100;
      const percentageFromCenterOfHeadY = (mouseDistanceFromCenterOfHeadY / centerOfHeadY) * 100;
      headRef.current?.style?.setProperty("--eye-x", `${percentageFromCenterOfHeadX / 2}%`);
      headRef.current?.style?.setProperty("--eye-y", `${percentageFromCenterOfHeadY / 2}%`);
      headRef.current?.style?.setProperty("--head-rotation", `${-1 * (percentageFromCenterOfHeadX / 4)}deg`);
    };
    document.addEventListener("mousemove", handleCursorPosition);
    return () => {
      document.removeEventListener("mousemove", handleCursorPosition);
    };
  }, []);

  // Auto-rotate head every 3 seconds
  useEffect(() => {
    headIntervalRef.current = setInterval(() => {
      setCurrentHead((prev) => getNextIndexRotating(prev, heads));
    }, 3000);

    return () => {
      if (headIntervalRef.current) {
        clearInterval(headIntervalRef.current);
      }
    };
  }, []);

  // Auto-rotate face every 3 seconds
  useEffect(() => {
    faceIntervalRef.current = setInterval(() => {
      setCurrentFace((prev) => getNextIndexRotating(prev, faces));
    }, 3000);

    return () => {
      if (faceIntervalRef.current) {
        clearInterval(faceIntervalRef.current);
      }
    };
  }, []);

  return (
    <AnimationContainer
      $columnWidths={columnWidths.center.current}
      ref={headRef}
    >
      {/* Blank head here to prevent flickering when the hat changes */}
      <InlineSVG
        className="head-blank"
        src={blankHead}
      />
      <InlineSVG
        className="head"
        src={heads[currentHead]}
        onClick={handleHeadClick}
      />
      <InlineSVG
        className="face"
        src={faces[currentFace]}
        onClick={handleFaceClick}
      />
      <InlineSVG
        className="sparkle one"
        src="/assets/small-sparkle.svg"
      />
      <InlineSVG
        className="sparkle two"
        src="/assets/big-sparkle.svg"
      />
      <InlineSVG
        className="sparkle three"
        src="/assets/small-sparkle.svg"
      />
    </AnimationContainer>
  );
}

const AnimationContainer = styled.div`
  @keyframes sparkle-scale {
    0% {
      transform: scale(0.8);
    }
    100% {
      transform: scale(1.5);
    }
  }

  width: 100%;
  height: 200px;
  display: flex;
  justify-content: center;
  align-items: flex-end;
  position: relative;
  overflow: hidden;
  --eye-x: 0%;
  --eye-y: 0%;
  --head-rotation: 0deg;
  --sparkle-size: 15px;
  --first-color: var(--primary-cta-light);
  --second-color: var(--primary-cta-hover);

  .head {
    position: absolute;
    cursor: pointer;
    margin-bottom: -120px;
    transform: rotate(var(--head-rotation));

    circle {
      opacity: 0;
    }

    path {
      fill: var(--second-color);
    }
  }

  .head-blank {
    position: absolute;
    margin-bottom: -120px;
    transform: translateX(-9.2%);

    circle {
      fill: var(--first-color);
      opacity: 0.25;
    }
  }

  .sparkle {
    position: absolute;
    left: 50%;
    top: 75%;
    animation: sparkle-scale 2s linear infinite alternate;
    path {
      fill: var(--first-color);
      opacity: 0.3;
    }
  }

  .sparkle.one {
    margin-left: -138px;
    width: var(--sparkle-size);
    height: var(--sparkle-size);
    animation-delay: 0s;
  }
  .sparkle.two {
    margin-left: -170px;
    margin-top: 15px;
    width: calc(var(--sparkle-size) * 1.8);
    height: calc(var(--sparkle-size) * 1.8);
    animation-delay: 1s;
  }
  .sparkle.three {
    margin-left: 120px;
    margin-top: -50px;
    animation-delay: 2s;
  }

  .face {
    cursor: pointer;
    position: absolute;
    bottom: 40px;
    transform: translate(var(--eye-x), var(--eye-y));
    path {
      fill: var(--second-color);
    }
  }
`;

export default CharacterGenerationAnimation;
